import React from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { LoginPanel } from '@weirgroup/one-weir';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import withAuthProvider from '../../services/Auth/AuthProvider';
import styles from './main.module.css';
import { userActions } from './actions';
import { utilRegx } from '../../resources/constants/index';
import { applicationRouteConstants } from '../../shared/_constants';

class LoginPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      user: null,
      loginFailed: false,
      email: '',
      formErrors: { email: '' },
      emailValidationState: '',
      buttonStateIsDisable: false,
      btnIsLoading: false,
    };
  }

  componentDidMount() {
    // Clear local storage but preserve MSAL state (needed for redirect authentication)
    // MSAL stores state in localStorage that must be preserved for redirect flow
    const msalKeys = [];
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('msal.')) {
        msalKeys.push({ key, value: localStorage.getItem(key) });
      } else {
        localStorage.removeItem(key);
      }
    });

    // Restore MSAL keys after clearing
    msalKeys.forEach(({ key, value }) => {
      localStorage.setItem(key, value);
    });

    console.log('LoginPage: Cleared localStorage but preserved MSAL state');

    window._fs_ready = () => {
      window.FS.shutdown();
    };
  }

  componentDidUpdate(prevProps) {
    const { error, isAuthenticated, isAuthorised, navigate } = this.props;
    const { btnIsLoading } = this.state;

    // Check if authentication was successful and redirect
    if (
      isAuthenticated &&
      isAuthorised &&
      (!prevProps.isAuthenticated || !prevProps.isAuthorised)
    ) {
      if (navigate) {
        navigate(applicationRouteConstants.HUB, {
          state: { isAuthenticated: true },
        });
      }
    }

    if (error && Object.keys(error).length > 0 && btnIsLoading) {
      this.setState({
        btnIsLoading: false,
        email: '',
      });
    }
  }

  handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      this.handleSubmit(e);
    }
  };

  handleChange = (e) => {
    this.setState({ email: e.trim() });
    this.updateValues('', 'success', false);
  };

  validate = () => {
    const { email } = this.state;
    const { t } = this.props;
    const isValid = utilRegx.EMAIL_VALID.test(String(email).toLowerCase());
    if (
      isValid &&
      (email.indexOf('@mail.weir', email.length - '@mail.weir'.length) !== -1 ||
        email.indexOf('@cloud.weir', email.length - '@cloud.weir'.length) !==
          -1)
    ) {
      this.updateValues('', 'success', false);
    } else {
      this.updateValues(t('LoginPageEmailHint'), 'error', true);
    }
  };

  handleSubmit = (e) => {
    const { login } = this.props;
    this.setState({ btnIsLoading: true });
    e.preventDefault();
    const { email, emailValidationHelpText } = this.state;
    this.setState({
      emailValidationState: email.trim() === '' ? 'error' : '',
    });
    if (email && emailValidationHelpText.length === 0) {
      login(email);
    }
  };

  updateValues(emailValHelpText, emailValState, buttonState) {
    this.setState({
      emailValidationHelpText: emailValHelpText,
      emailValidationState: emailValState,
      buttonStateIsDisable: buttonState,
    });
  }

  render() {
    const { t } = this.props;
    const {
      emailValidationState,
      emailValidationHelpText,
      email,
      buttonStateIsDisable,
      btnIsLoading,
    } = this.state;
    return (
      <div className={styles.weirBackground}>
        <div className={styles.layer}>
          <div className={styles.wrapper}>
            <LoginPanel
              label={t('LoginPageLoginTitle')}
              inputlabel={t('LoginPageEmailTitle')}
              inputPlaceHolder={t('LoginPageEmailPholder')}
              buttonTitle={t('LoginPageButtonTitleLogin')}
              onClick={this.handleSubmit}
              onChange={this.handleChange}
              emailValidationState={emailValidationState}
              emailValidationHelpText={emailValidationHelpText}
              email={email}
              buttonState={buttonStateIsDisable}
              btnIsLoading={btnIsLoading}
              onBlur={this.validate}
              requiredLabel={t('LoginPageRequiredLabel')}
            />
            <div className={styles.loginContentAccess}>
              {t('LoginContentAccess')}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    isAuthenticated: state.AuthReducer.isAuthenticated,
    isAuthorised: state.AuthReducer.isAuthorised,
  };
};

function mapDispatchToProps(dispatch) {
  return {
    userActionsUpdate: (status) => {
      dispatch(userActions(status));
    },
  };
}

// HOC to inject navigate and location hooks for class components
function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    const navigate = useNavigate();
    const location = useLocation();
    return <Component {...props} navigate={navigate} location={location} />;
  }
  return ComponentWithRouterProp;
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(withRouter(withAuthProvider(LoginPage))));

LoginPage.propTypes = {
  t: PropTypes.func.isRequired,
  login: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  isAuthorised: PropTypes.bool,
};

LoginPage.defaultProps = {
  isAuthenticated: false,
  isAuthorised: false,
};
